// src/components/EquipmentManager/ReviewCard.jsx

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  FaThumbsUp, 
  FaThumbsDown, 
  FaFlag, 
  FaCheckCircle,
  FaClock,
  FaReply,
  FaEdit,
  FaTrash
} from 'react-icons/fa';
import StarRating from '../common/StarRating';
import { useNotification } from '../../context/NotificationContext';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import styles from './ReviewCard.module.css';

const ReviewCard = ({ 
  review, 
  currentUserId, 
  canManage = false,
  onVote,
  onDelete,
  onEdit
}) => {
  const { showSuccess, showError } = useNotification();
  const [userVote, setUserVote] = useState(null); // 'helpful', 'not-helpful', or null
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReportConfirm, setShowReportConfirm] = useState(false);
  
  const isOwnReview = review.userId === currentUserId || review.userId?._id === currentUserId;
  
  const handleVoteClick = async (voteType) => {
    if (isOwnReview) {
      showError('You cannot vote on your own review');
      return;
    }
    
    try {
      const newVote = userVote === voteType ? 'remove' : voteType;
      
      await onVote(review._id, newVote);
      setUserVote(newVote === 'remove' ? null : newVote);
      
      if (newVote === 'remove') {
        showSuccess('Vote removed');
      } else {
        showSuccess(`Marked as ${voteType === 'helpful' ? 'helpful' : 'not helpful'}`);
      }
    } catch (error) {
      showError('Failed to vote on review');
    }
  };
  
  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };
  
  const confirmDelete = async () => {
    try {
      await onDelete(review._id);
      showSuccess('Review deleted successfully');
      setShowDeleteConfirm(false);
    } catch (error) {
      showError('Failed to delete review');
    }
  };
  
  const handleReportClick = () => {
    setShowReportConfirm(true);
  };
  
  const confirmReport = async () => {
    try {
      // Report logic would go here
      showSuccess('Review reported. Thank you for your feedback.');
      setShowReportConfirm(false);
    } catch (error) {
      showError('Failed to report review');
    }
  };
  
  const formatDate = (date) => {
    const reviewDate = new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now - reviewDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };
  
  return (
    <div className={styles.reviewCard}>
      {/* Header */}
      <div className={styles.reviewHeader}>
        <div className={styles.userInfo}>
          <div className={styles.avatar}>
            {(review.userInfo?.name || 'U').charAt(0).toUpperCase()}
          </div>
          <div className={styles.userDetails}>
            <div className={styles.userName}>
              {review.userInfo?.name || 'Anonymous User'}
              {review.wasAssigned && (
                <span className={styles.verifiedBadge} title="Verified - Assigned this equipment">
                  <FaCheckCircle /> Verified
                </span>
              )}
            </div>
            <div className={styles.reviewDate}>
              {formatDate(review.createdAt)}
              {review.isEdited && <span className={styles.edited}> (edited)</span>}
            </div>
          </div>
        </div>
        
        <div className={styles.reviewRating}>
          <StarRating rating={review.rating} size="small" />
        </div>
      </div>
      
      {/* Content */}
      <div className={styles.reviewContent}>
        <h4 className={styles.reviewTitle}>{review.title}</h4>
        <p className={styles.reviewText}>{review.reviewText}</p>
        
        {/* Usage Duration */}
        {review.usageDuration && (
          <div className={styles.usageDuration}>
            <FaClock className={styles.usageIcon} />
            <span>
              Used for {review.usageDuration.value} {review.usageDuration.unit}
            </span>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className={styles.reviewFooter}>
        {/* Helpful Votes */}
        <div className={styles.voteButtons}>
          <button 
            onClick={() => handleVoteClick('helpful')}
            className={`${styles.voteBtn} ${userVote === 'helpful' ? styles.voted : ''}`}
            disabled={isOwnReview}
            title={isOwnReview ? 'Cannot vote on own review' : 'Mark as helpful'}
          >
            <FaThumbsUp /> 
            Helpful ({review.helpfulCount || 0})
          </button>
          <button 
            onClick={() => handleVoteClick('not-helpful')}
            className={`${styles.voteBtn} ${userVote === 'not-helpful' ? styles.voted : ''}`}
            disabled={isOwnReview}
            title={isOwnReview ? 'Cannot vote on own review' : 'Mark as not helpful'}
          >
            <FaThumbsDown />
            ({review.notHelpfulCount || 0})
          </button>
        </div>
        
        {/* Actions */}
        <div className={styles.actions}>
          {isOwnReview ? (
            <>
              {onEdit && (
                <button 
                  onClick={() => onEdit(review)}
                  className={styles.editBtn}
                  title="Edit Review"
                >
                  <FaEdit /> Edit
                </button>
              )}
              {onDelete && (
                <button 
                  onClick={handleDeleteClick}
                  className={styles.deleteBtn}
                  title="Delete Review"
                >
                  <FaTrash /> Delete
                </button>
              )}
            </>
          ) : (
            <>
              {!isOwnReview && (
                <button 
                  onClick={handleReportClick}
                  className={styles.reportBtn}
                  title="Report Review"
                >
                  <FaFlag /> Report
                </button>
              )}
              {canManage && onDelete && (
                <button 
                  onClick={handleDeleteClick}
                  className={styles.deleteBtn}
                  title="Delete Review (Manager)"
                >
                  <FaTrash /> Delete
                </button>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Sponsor Response */}
      {review.sponsorResponse && (
        <div className={styles.sponsorResponse}>
          <div className={styles.responseHeader}>
            <FaReply className={styles.responseIcon} />
            <span className={styles.responseTitle}>
              Response from {review.sponsorResponse.respondedBy?.name || 'Management'}
            </span>
            <span className={styles.responseDate}>
              {formatDate(review.sponsorResponse.respondedAt)}
            </span>
          </div>
          <p className={styles.responseText}>{review.sponsorResponse.text}</p>
        </div>
      )}
      
      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Delete Review"
        message="Are you sure you want to delete this review? This action cannot be undone."
      />
      
      {/* Report Confirmation */}
      <ConfirmationModal
        isOpen={showReportConfirm}
        onClose={() => setShowReportConfirm(false)}
        onConfirm={confirmReport}
        title="Report Review"
        message="Are you sure you want to report this review as inappropriate?"
      />
    </div>
  );
};

ReviewCard.propTypes = {
  review: PropTypes.object.isRequired,
  currentUserId: PropTypes.string,
  canManage: PropTypes.bool,
  onVote: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func
};

export default ReviewCard;

